import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import '../CSS/PlacementNewsPage.css';

interface Placement {
  placementId: number;
  placementTitle: string;
  placementSource: string;
  placementSourceIcon: string;
  placementPicture: string;
  createdDate: string;
  lastUpdated: string;
  placementShortDesc: string;
  placementLongDesc: string;
}

const PlacementNewsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [placement, setPlacement] = useState<Placement | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [title, setTitle] = useState<string>(''); // Will be populated from database

  useEffect(() => {
    // Fetch placement data from database
    const fetchPlacement = async () => {
      if (!id) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        // Production API endpoint
        // const response = await fetch(`/api/placements/${id}`);
        const response = await fetch(`/api/placements/${id}`);
        const data = await response.json();

        if (data.success && data.data) {
          setPlacement(data.data);
          setTitle(data.data.placementTitle || ''); // Set title from database
        } else {
          // If no placement found, leave title as empty default
          setTitle('');
        }
      } catch (error) {
        console.error('Error fetching placement:', error);
        setTitle(''); // Leave as empty default on error
      } finally {
        setIsLoading(false);
      }
    };

    fetchPlacement();
  }, [id]);

  // Helper function to convert database file path to API endpoint URL
  const getImageUrl = (path: string | null | undefined): string => {
    if (!path || path.trim() === '') return '/placeholder-image.jpg';
    // If path already starts with http, return as is
    if (path.startsWith('http://') || path.startsWith('https://')) {
      return path;
    }
    // Convert database path to API endpoint
    // Database path: Files/Placement Images/filename.png
    // API endpoint: /api/placements/file/Files/Placement Images/filename.png
    const cleanPath = path.startsWith('/') ? path.slice(1) : path;
    // Production API endpoint
    // return `/api/placements/file/${cleanPath}`;
    return `/api/placements/file/${cleanPath}`;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="placement-news-page">
      {/* Breadcrumb Navigation */}
      <div className="placement-news-breadcrumb">
        <Link to="/">Home</Link>
        <span className="breadcrumb-separator">/</span>
        <Link to="/news-and-insights/recent-placements" className="breadcrumb-news-insights">News & Insights</Link>
        <span className="breadcrumb-ellipsis-mobile">...</span>
        <span className="breadcrumb-separator">/</span>
        <Link to="/news-and-insights/recent-placements">Recent Placements</Link>
        <span className="breadcrumb-separator">/</span>
        {title && (
          <>
            <span className="breadcrumb-current">{title}</span>
          </>
        )}
      </div>

      {/* Placement Content */}
      <div className="placement-news-content">
        {isLoading ? (
          <div className="placement-news-loading">Loading...</div>
        ) : placement ? (
          <div className="placement-news-layout">
            {placement.placementPicture && (
              <div className="placement-news-image">
                <img
                  src={getImageUrl(placement.placementPicture)}
                  alt={placement.placementTitle}
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src = '/placeholder-image.jpg';
                  }}
                />
              </div>
            )}
            <div className="placement-news-text">
              <div className="placement-news-date">
                {formatDate(placement.createdDate)}
              </div>
              <h1 className="placement-news-title">{placement.placementTitle}</h1>
              <div className="placement-news-source">
                <span className="placement-source-label">Source:</span>
                {placement.placementSourceIcon && (
                  <img
                    src={getImageUrl(placement.placementSourceIcon)}
                    alt={placement.placementSource}
                    className="placement-source-icon"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                    }}
                  />
                )}
                <span className="placement-source-text">
                  {placement.placementSource}
                </span>
              </div>
              <div 
                className="placement-news-body"
                dangerouslySetInnerHTML={{ __html: placement.placementLongDesc }}
              />
            </div>
          </div>
        ) : (
          <div className="placement-news-error">
            Placement article not found
          </div>
        )}
      </div>
    </div>
  );
};

export default PlacementNewsPage;

